import React, { useState } from "react";
import {
  Calendar,
  DollarSign,
  Star,
  TrendingUp,
  BarChart3,
  Package,
  ArrowUpRight,
  ArrowDownRight,
  ListChecks,
} from "lucide-react";
import { useVendorAnalytics } from "../../services/vendorAnalyticsService";
import FormattedDate from "../../components/shared/FormattedDate";
import ChannelFilterTabs from "../../components/vendor/insights/ChannelFilterTabs";
import ChannelSplitCards from "../../components/vendor/insights/ChannelSplitCards";
import ChannelBadge from "../../components/shared/ChannelBadge";
import {
  VendorPageHeader,
  VendorGuideCallout,
} from "../../components/vendor/guidance";
import { VENDOR_PAGE_GUIDES } from "../../utils/vendorPageGuides";

const PERIOD_TABS = [
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "quarter", label: "This Quarter" },
  { value: "year", label: "This Year" },
];

const VendorAnalytics = () => {
  const [period, setPeriod] = useState("month");
  const [channel, setChannel] = useState("all");

  const { data, isLoading, error } = useVendorAnalytics(period, channel);

  const analytics = data?.data || {};

  const formatValue = (value, formatter) => {
    if (value === null || value === undefined || isNaN(value)) {
      return "N/A";
    }
    return formatter(value);
  };

  const keyMetrics = [
    {
      label: "Total Bookings",
      value: analytics.total_bookings || 0,
      icon: Calendar,
      color: "text-ink",
      bgColor: "bg-surface-muted border-border",
    },
    {
      label: channel === "external" ? "Collectible revenue" : "Total Revenue",
      value: formatValue(
        analytics.total_revenue,
        (val) =>
          `$${Number(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      ),
      icon: DollarSign,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50 border-emerald-100",
    },
    {
      label: "Average Rating",
      value: formatValue(
        analytics.average_rating,
        (val) => `${Number(val).toFixed(1)}/5`,
      ),
      icon: Star,
      color: "text-amber-600",
      bgColor: "bg-amber-50 border-amber-100",
    },
    {
      label: "Total Reviews",
      value: analytics.total_reviews || 0,
      icon: BarChart3,
      color: "text-ink",
      bgColor: "bg-surface-muted border-border",
    },
  ];

  const secondaryMetrics = [
    {
      label: "Booking Conversion Rate",
      value: formatValue(
        analytics.booking_conversion_rate,
        (val) => `${Number(val).toFixed(1)}%`,
      ),
      description:
        channel === "external"
          ? "Marketplace conversion (Internal). External jobs are logged manually."
          : "Percentage of inquiries converted to bookings (Internal)",
    },
    {
      label: "Average Booking Value",
      value: formatValue(
        analytics.average_booking_value,
        (val) =>
          `$${Number(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      ),
      description: "Average revenue per completed booking in this channel filter",
    },
  ];

  const cardClass = "product-panel p-4 sm:p-6";

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Analytics"
        subtitle="Internal marketplace and External private bookings — switch channel or period to compare."
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.analytics.title}
        steps={VENDOR_PAGE_GUIDES.analytics.steps}
        icon={ListChecks}
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div
          className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border"
          role="tablist"
          aria-label="Analytics period"
        >
          {PERIOD_TABS.map((tab) => {
            const isActive = period === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setPeriod(tab.value)}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${isActive ? "bg-surface text-ink shadow-sm" : "text-ink-secondary hover:text-gray-900"}`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <ChannelFilterTabs value={channel} onChange={setChannel} />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={`${cardClass} animate-pulse`}>
              <div className="h-4 bg-gray-200 rounded w-24 mb-4" />
              <div className="h-8 bg-gray-200 rounded w-32" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-600 px-4 py-3 text-sm">
          Failed to load analytics data. Please try again.
        </div>
      ) : (
        <>
          {channel === "all" && (
            <ChannelSplitCards byChannel={analytics.by_channel} />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {keyMetrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <div key={index} className={cardClass}>
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className={`w-12 h-12 border ${metric.bgColor} rounded-xl flex items-center justify-center`}
                    >
                      <Icon className={`w-6 h-6 ${metric.color}`} />
                    </div>
                    {metric.change !== null &&
                      metric.change !== undefined &&
                      !isNaN(metric.change) && (
                        <div
                          className={`flex items-center gap-1 text-sm ${metric.change >= 0 ? "text-emerald-600" : "text-red-600"}`}
                        >
                          {metric.change >= 0 ? (
                            <ArrowUpRight className="w-4 h-4" />
                          ) : (
                            <ArrowDownRight className="w-4 h-4" />
                          )}
                          <span>{Math.abs(metric.change).toFixed(1)}%</span>
                        </div>
                      )}
                  </div>
                  <p className="text-sm text-ink-secondary mb-2">
                    {metric.label}
                  </p>
                  <p className="text-2xl font-bold tracking-tight text-ink tabular-nums">
                    {metric.value}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {secondaryMetrics.map((metric, index) => (
              <div key={index} className={cardClass}>
                <p className="text-sm text-ink-secondary mb-1">
                  {metric.label}
                </p>
                <p className="text-2xl font-bold tracking-tight text-ink mb-2 tabular-nums">
                  {metric.value}
                </p>
                <p className="text-xs text-ink-tertiary">
                  {metric.description}
                </p>
              </div>
            ))}
          </div>

          {analytics.top_services && analytics.top_services.length > 0 && (
            <div className={cardClass}>
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-ink-tertiary" />
                <h2 className="text-lg font-semibold text-ink">
                  Top Performing Services
                </h2>
              </div>
              <div className="space-y-3">
                {analytics.top_services.map((service, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-xl border border-border bg-surface-muted hover:border-ink transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-ink truncate">
                          {service.service_name || service.name}
                        </p>
                        {service.channel ? (
                          <ChannelBadge channel={service.channel} />
                        ) : null}
                      </div>
                      <p className="text-sm text-ink-tertiary">
                        {service.booking_count || service.bookings_count || 0}{" "}
                        bookings
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-semibold text-ink tabular-nums">
                        $
                        {Number(
                          service.total_revenue || service.revenue || 0,
                        ).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                      <p className="text-xs text-ink-tertiary">revenue</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {analytics.booking_trends && analytics.booking_trends.length > 0 && (
            <div className={`${cardClass} overflow-hidden`}>
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-ink-tertiary" />
                <h2 className="text-lg font-semibold text-ink">
                  Booking Trends
                </h2>
              </div>
              <div className="overflow-x-auto -mx-1">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-medium text-ink-secondary">
                        Period
                      </th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-ink-secondary">
                        Bookings
                      </th>
                      {channel === "all" && (
                        <>
                          <th className="text-right py-3 px-4 text-sm font-medium text-ink-secondary">
                            Internal
                          </th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-ink-secondary">
                            External
                          </th>
                        </>
                      )}
                      <th className="text-right py-3 px-4 text-sm font-medium text-ink-secondary">
                        Revenue
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.booking_trends.map((trend, index) => (
                      <tr
                        key={index}
                        className="border-b border-border hover:bg-surface-muted/80"
                      >
                        <td className="py-3 px-4 text-sm text-ink">
                          <FormattedDate date={trend.period} format="auto" />
                        </td>
                        <td className="py-3 px-4 text-sm text-ink-secondary text-right tabular-nums">
                          {trend.booking_count || 0}
                        </td>
                        {channel === "all" && (
                          <>
                            <td className="py-3 px-4 text-sm text-ink-secondary text-right tabular-nums">
                              {trend.internal_bookings ?? 0}
                            </td>
                            <td className="py-3 px-4 text-sm text-ink-secondary text-right tabular-nums">
                              {trend.external_bookings ?? 0}
                            </td>
                          </>
                        )}
                        <td className="py-3 px-4 text-sm font-medium text-ink text-right tabular-nums">
                          $
                          {Number(trend.revenue || 0).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(!analytics.top_services || analytics.top_services.length === 0) &&
            (!analytics.booking_trends ||
              analytics.booking_trends.length === 0) && (
              <div className={`${cardClass} p-8 sm:p-12 text-center`}>
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-surface-muted text-gray-400">
                  <BarChart3 className="w-7 h-7" />
                </div>
                <p className="text-ink-secondary">
                  No analytics data available for this period
                </p>
                <p className="text-sm text-ink-tertiary mt-2">
                  Start getting bookings to see your analytics
                </p>
              </div>
            )}
        </>
      )}
    </div>
  );
};

export default VendorAnalytics;
